import { useState, useEffect } from 'react';

/**
 * Custom Hook to detect screen size and device type
 * @returns {Object} - Device type and screen size information
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isXs: false,
    isSm: false,
    isMd: false,
    isLg: false,
    isXl: false,
    isXxl: false
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      setScreenSize({
        width,
        height: window.innerHeight,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 992,
        isDesktop: width >= 992,
        isXs: width < 576,
        isSm: width >= 576 && width < 768,
        isMd: width >= 768 && width < 992,
        isLg: width >= 992 && width < 1200,
        isXl: width >= 1200 && width < 1400,
        isXxl: width >= 1400
      });
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

/**
 * Responsive breakpoint values
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400
};

/**
 * Check if current screen is mobile
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < BREAKPOINTS.md;
};

/**
 * Check if current screen is tablet
 */
export const isTabletDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= BREAKPOINTS.md && window.innerWidth < BREAKPOINTS.lg;
};

/**
 * Check if current screen is desktop
 */
export const isDesktopDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= BREAKPOINTS.lg;
};

/**
 * Get current breakpoint name
 */
export const getCurrentBreakpoint = () => {
  if (typeof window === 'undefined') return 'lg';
  
  const width = window.innerWidth;
  
  if (width < BREAKPOINTS.sm) return 'xs';
  if (width < BREAKPOINTS.md) return 'sm';
  if (width < BREAKPOINTS.lg) return 'md';
  if (width < BREAKPOINTS.xl) return 'lg';
  if (width < BREAKPOINTS.xxl) return 'xl';
  return 'xxl';
};

/**
 * Component to conditionally render based on screen size
 */
export const ShowOn = ({ xs, sm, md, lg, xl, xxl, children }) => {
  const screen = useResponsive();
  
  const shouldShow = (
    (xs && screen.isXs) ||
    (sm && screen.isSm) ||
    (md && screen.isMd) ||
    (lg && screen.isLg) ||
    (xl && screen.isXl) ||
    (xxl && screen.isXxl)
  );
  
  return shouldShow ? children : null;
};

/**
 * Component to hide on specific screen sizes
 */
export const HideOn = ({ xs, sm, md, lg, xl, xxl, children }) => {
  const screen = useResponsive();
  
  const shouldHide = (
    (xs && screen.isXs) ||
    (sm && screen.isSm) ||
    (md && screen.isMd) ||
    (lg && screen.isLg) ||
    (xl && screen.isXl) ||
    (xxl && screen.isXxl)
  );
  
  return !shouldHide ? children : null;
};

/**
 * Component to show only on mobile
 */
export const MobileOnly = ({ children }) => {
  const { isMobile } = useResponsive();
  return isMobile ? children : null;
};

/**
 * Component to show only on tablet
 */
export const TabletOnly = ({ children }) => {
  const { isTablet } = useResponsive();
  return isTablet ? children : null;
};

/**
 * Component to show only on desktop
 */
export const DesktopOnly = ({ children }) => {
  const { isDesktop } = useResponsive();
  return isDesktop ? children : null;
};

export default {
  useResponsive,
  BREAKPOINTS,
  isMobileDevice,
  isTabletDevice,
  isDesktopDevice,
  getCurrentBreakpoint,
  ShowOn,
  HideOn,
  MobileOnly,
  TabletOnly,
  DesktopOnly
};

